import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";

interface OcrResultModalProps {
  open: boolean;
  onClose: () => void;
  text: string;
  onInsertText?: (text: string) => void;
}

export default function OcrResultModal({
  open,
  onClose,
  text,
  onInsertText,
}: OcrResultModalProps) {
  const { t } = useTranslation(["bugherd"]);
  const [showCopied, setShowCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setShowCopied(true);
  };

  const handleInsert = () => {
    onInsertText?.(text);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" component="span">
            {t("ocrResult")}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {text ? (
            <TextField
              multiline
              fullWidth
              minRows={4}
              maxRows={12}
              value={text}
              slotProps={{ input: { readOnly: true } }}
              sx={{ mt: 1 }}
            />
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {t("noTextFound")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {onInsertText && text && (
            <Button
              onClick={handleInsert}
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
            >
              {t("insertIntoDescription")}
            </Button>
          )}
          {text && (
            <Button
              onClick={handleCopy}
              startIcon={<ContentCopyIcon />}
              variant="contained"
              size="small"
            >
              {t("copyToClipboard")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showCopied}
        autoHideDuration={2000}
        onClose={() => setShowCopied(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setShowCopied(false)}>
          {t("copiedToClipboard")}
        </Alert>
      </Snackbar>
    </>
  );
}
